import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useInternalShipments } from '../hooks/useInternalShipments';
import { InternalShipmentStatus } from '../services/internalShipmentService';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Input, InputGroup, InputAddon, Select } from '../components/ui/Input';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Icon } from '../components/ui/Icon';

const statusOptions: { value: InternalShipmentStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusTone = (s: string): 'ok' | 'warn' | 'err' | 'info' | 'default' => {
  switch (s) {
    case 'delivered': return 'ok';
    case 'pending_review': return 'warn';
    case 'rejected':
    case 'cancelled': return 'err';
    case 'in_transit':
    case 'approved': return 'info';
    default: return 'default';
  }
};

const InternalShipmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<InternalShipmentStatus | 'all'>('all');

  const filters = useMemo(
    () => ({ search: search || undefined, status, per_page: 50 }),
    [search, status]
  );
  const { shipments, isLoading, error } = useInternalShipments(filters);

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <PageTitle>Internal shipments</PageTitle>
          <PageSub>Move inventory between your warehouses.</PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="blue" onClick={() => navigate('/internal-shipments/new')}>
            <Icon name="plus" size={14} />
            <span>New internal shipment</span>
          </Button>
        </div>
      </PageHead>

      <Card style={{ marginBottom: 24 }}>
        <div className="card-header" style={{ gap: 12, flexWrap: 'wrap' }}>
          <InputGroup style={{ maxWidth: 320, flex: 1 }}>
            <InputAddon><Icon name="search" size={13} /></InputAddon>
            <Input
              placeholder="Search shipment number, item, tracking…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
          <Field style={{ minWidth: 220 }}>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as InternalShipmentStatus | 'all')}
              aria-label="Status filter"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </Field>
        </div>

        {error && (
          <div style={{ padding: 16 }}>
            <Callout tone="warn">
              <Icon name="alertCircle" size={16} />
              <span>Failed to load internal shipments.</span>
            </Callout>
          </div>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Shipment</th>
              <th>Recipient</th>
              <th>Item</th>
              <th>Cost</th>
              <th>Status</th>
              <th>Created</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7}>
                  <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    Loading shipments…
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && shipments.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    No internal shipments found.
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && shipments.map((s) => (
              <tr
                key={s.id}
                className="clickable"
                onClick={() => navigate(`/internal-shipments/${s.id}`)}
              >
                <td>
                  <span className="mono" style={{ fontSize: 13 }}>{s.shipmentNumber}</span>
                  <div className="muted" style={{ fontSize: 12 }}>{s.sender.contactName}</div>
                </td>
                <td>
                  <div>{s.recipient.contactName}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {s.recipient.state?.name || s.recipient.cityName || 'No location'}
                  </div>
                </td>
                <td>{s.itemDescription}</td>
                <td className="mono">
                  {s.currency} {s.quotedCost}
                </td>
                <td>
                  <Badge tone={statusTone(s.status)} dot>
                    {s.statusLabel}
                  </Badge>
                </td>
                <td style={{ fontSize: 13 }}>
                  {s.createdAt ? format(new Date(s.createdAt), 'MMM d, yyyy') : 'N/A'}
                </td>
                <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/internal-shipments/${s.id}`)}
                  >
                    <Icon name="eye" size={13} />
                    <span>View</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Page>
  );
};

export default InternalShipmentsPage;
